import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { normalizeGlobalEntryLocale, readGlobalEntryHandoff } from "../utils/navigation/globalEntryHandoff";

/**
 * Applies the `lng` handoff from global-entry once per distinct URL locale.
 *
 * This gives arriving users the expected initial language while still letting
 * them change locale manually afterward without being forced back on every
 * route navigation.
 *
 * @returns {void}
 */
export function useGlobalEntryLocale() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lastAppliedLocaleRef = useRef(null);

  useEffect(() => {
    const handoff = readGlobalEntryHandoff(location.search);

    if (!handoff.lng) {
      return;
    }

    if (lastAppliedLocaleRef.current === handoff.lng) {
      return;
    }

    lastAppliedLocaleRef.current = handoff.lng;

    const currentLocale = normalizeGlobalEntryLocale(
      i18n.resolvedLanguage || i18n.language,
    );

    if (currentLocale === handoff.lng) {
      return;
    }

    void i18n.changeLanguage(handoff.lng);
  }, [i18n, location.search]);
}
